import React, { useState, useEffect } from 'react';
import { doc, onSnapshot, updateDoc, arrayUnion, arrayRemove, deleteField } from 'firebase/firestore';
import { db } from '../../servicios/firebase';
import BotonVolver from '../../components/ui/BotonVolver';
import ModalSeguridad from '../../components/ui/ModalSeguridad';

const Terminales = () => {
  const [gruposTerminales, setGruposTerminales] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // --- ESTADOS CREACIÓN ---
  const [zona, setZona] = useState(""); 
  const [nuevaTerminal, setNuevaTerminal] = useState("");

  // --- ESTADOS EDICIÓN (UPDATE) ---
  const [editando, setEditando] = useState(null); // Objeto { zona, nombreOriginal, nuevoNombre }
  
  // --- ESTADOS ELIMINACIÓN (DELETE) ---
  const [modalOpen, setModalOpen] = useState(false);
  const [datosEliminar, setDatosEliminar] = useState({ zona: '', terminal: '', esZonaEntera: false });

  // 1. LEER (READ) - En tiempo real
  useEffect(() => {
    const docRef = doc(db, 'configuraciones', 'terminales');
    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        const listaProcesada = [];

        Object.entries(data).forEach(([nombreZona, contenido]) => {
            if (nombreZona === 'activo') return; 
            if (Array.isArray(contenido)) {
                listaProcesada.push({
                    zona: nombreZona,
                    terminales: contenido.sort() // Ordenamos terminales A-Z
                });
            }
        });
        
        // Ordenar Zonas A-Z
        listaProcesada.sort((a, b) => a.zona.localeCompare(b.zona));
        setGruposTerminales(listaProcesada);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // 2. CREAR (CREATE)
  const agregarTerminal = async (e) => {
    e.preventDefault();
    if (!zona.trim() || !nuevaTerminal.trim()) return alert("Completa ambos campos");
    
    const zonaKey = zona.trim(); 
    const terminalValor = nuevaTerminal.trim();

    try {
      const docRef = doc(db, 'configuraciones', 'terminales');
      await updateDoc(docRef, {
        [zonaKey]: arrayUnion(terminalValor)
      });
      setNuevaTerminal("");
    } catch (error) {
      console.error(error);
      alert("Error al guardar");
    }
  };

  // 3. ACTUALIZAR (UPDATE) - Renombrar Terminal
  const iniciarEdicion = (zona, nombre) => {
      setEditando({ 
          zona: zona, 
          nombreOriginal: nombre, 
          nuevoNombre: nombre 
      });
  };

  const guardarEdicion = async (e) => {
      e.preventDefault();
      if (!editando || !editando.nuevoNombre.trim()) return;

      try {
          // A) Obtenemos la lista actual de esa zona desde nuestro estado local (que está sincronizado)
          const grupoActual = gruposTerminales.find(g => g.zona === editando.zona);
          if (!grupoActual) return;

          // B) Creamos la nueva lista modificando solo el nombre que cambió
          const nuevaLista = grupoActual.terminales.map(term => 
              term === editando.nombreOriginal ? editando.nuevoNombre.trim() : term
          );

          // C) Guardamos la lista entera reemplazando la vieja
          const docRef = doc(db, 'configuraciones', 'terminales');
          await updateDoc(docRef, {
              [editando.zona]: nuevaLista
          });

          setEditando(null); // Cerrar modal
      } catch (error) {
          console.error(error);
          alert("Error al editar");
      }
  };

  // 4. BORRAR (DELETE) - Terminal Individual o Zona Entera
  const solicitarBorrado = (zona, terminal = null) => {
      if (terminal) {
          // Borrar solo una terminal
          setDatosEliminar({ zona, terminal, esZonaEntera: false });
      } else {
          // Borrar toda la carpeta (zona)
          setDatosEliminar({ zona, terminal: '', esZonaEntera: true });
      }
      setModalOpen(true);
  };

  const confirmarBorrado = async () => {
    try {
      const docRef = doc(db, 'configuraciones', 'terminales');
      
      if (datosEliminar.esZonaEntera) {
          // Borrar campo completo (Zona)
          await updateDoc(docRef, {
              [datosEliminar.zona]: deleteField()
          });
      } else {
          // Borrar elemento del array (Terminal)
          await updateDoc(docRef, {
              [datosEliminar.zona]: arrayRemove(datosEliminar.terminal)
          });
      }
      
      setModalOpen(false);
      setDatosEliminar({ zona: '', terminal: '', esZonaEntera: false });
    } catch (error) {
        console.error(error);
        alert("Error al eliminar");
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto min-h-screen bg-gray-50">
      <div className="mb-8 bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-wrap justify-between items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Terminales y Zonas</h1>
          <p className="text-gray-500">Gestión completa de ubicaciones (CRUD).</p>
        </div>
        <BotonVolver />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* --- FORMULARIO CREAR --- */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200 h-fit lg:col-span-1">
          <h2 className="font-bold text-gray-700 mb-4 border-b pb-2">➕ Agregar Ubicación</h2>
          <form onSubmit={agregarTerminal} className="space-y-5">
            <div>
              <label className="block text-xs font-bold text-blue-600 uppercase mb-1">Zona / Región</label>
              <input 
                list="zonas-existentes" type="text" placeholder="Ej: Mixteca"
                className="w-full p-3 border rounded-xl bg-blue-50 focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none font-bold text-gray-700 transition"
                value={zona} onChange={e => setZona(e.target.value)}
              />
              <datalist id="zonas-existentes">
                  {gruposTerminales.map(g => <option key={g.zona} value={g.zona} />)}
              </datalist>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Nombre Terminal</label>
              <input 
                type="text" placeholder="Ej: Terminal Centro"
                className="w-full p-3 border rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none transition"
                value={nuevaTerminal} onChange={e => setNuevaTerminal(e.target.value)}
              />
            </div>
            <button className="w-full bg-gray-900 text-white font-bold py-3 rounded-xl hover:bg-black transition shadow-lg">Guardar</button>
          </form>
        </div>

        {/* --- LISTADO (READ, UPDATE, DELETE) --- */}
        <div className="lg:col-span-2 space-y-6">
          {loading && <p className="text-center text-gray-400">Cargando catálogo...</p>}
          
          {!loading && gruposTerminales.length === 0 && (
              <div className="text-center p-10 bg-white rounded-2xl border border-dashed text-gray-400">
                  Base de datos vacía. Agrega tu primera zona.
              </div>
          )}

          {gruposTerminales.map((grupo) => (
            <div key={grupo.zona} className="bg-white p-5 rounded-2xl shadow-sm border border-gray-200 animate-fade-in-up">
                
                {/* CABECERA DE ZONA */}
                <div className="flex justify-between items-center mb-4 border-b pb-2">
                    <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                        🌍 {grupo.zona}
                        <span className="bg-gray-100 text-gray-500 text-xs px-2 py-1 rounded-full">{grupo.terminales.length}</span>
                    </h3>
                    <button 
                        onClick={() => solicitarBorrado(grupo.zona)}
                        className="text-xs text-red-400 hover:text-red-600 font-bold border border-red-100 px-2 py-1 rounded hover:bg-red-50 transition"
                    >
                        Eliminar Zona
                    </button>
                </div>
                
                {/* LISTA DE TERMINALES */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {grupo.terminales.map((term, idx) => (
                        <div key={idx} className="flex justify-between items-center p-3 bg-gray-50 rounded-xl border border-gray-100 hover:border-blue-300 transition group">
                            <span className="text-gray-700 font-medium pl-2">{term}</span>
                            <div className="flex gap-1">
                                {/* BOTÓN EDITAR */}
                                <button 
                                    onClick={() => iniciarEdicion(grupo.zona, term)}
                                    className="p-2 text-blue-300 hover:text-blue-600 transition" 
                                    title="Renombrar"
                                >
                                    ✏️
                                </button>
                                {/* BOTÓN BORRAR */}
                                <button 
                                    onClick={() => solicitarBorrado(grupo.zona, term)}
                                    className="p-2 text-gray-300 hover:text-red-500 transition"
                                    title="Eliminar"
                                >
                                    🗑️
                                </button>
                            </div>
                        </div>
                    ))}
                    {grupo.terminales.length === 0 && <p className="text-xs text-gray-400 italic p-2">Zona vacía.</p>}
                </div>
            </div>
          ))}
        </div>
      </div>

      {/* --- MODAL DE EDICIÓN --- */}
      {editando && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md animate-scale-in">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Renombrar Terminal</h3>
                <form onSubmit={guardarEdicion}>
                    <label className="text-xs font-bold text-gray-400 uppercase mb-1 block">Nombre Actual</label>
                    <div className="p-3 bg-gray-100 rounded-xl text-gray-600 mb-4 font-medium">{editando.nombreOriginal}</div>

                    <label className="text-xs font-bold text-blue-600 uppercase mb-1 block">Nuevo Nombre</label>
                    <input 
                        autoFocus
                        type="text" 
                        className="w-full p-3 border-2 border-blue-100 rounded-xl focus:border-blue-500 outline-none font-bold text-gray-800 mb-6"
                        value={editando.nuevoNombre}
                        onChange={e => setEditando({...editando, nuevoNombre: e.target.value})}
                    />
                    
                    <div className="flex gap-3">
                        <button type="button" onClick={() => setEditando(null)} className="flex-1 py-3 bg-gray-100 text-gray-600 font-bold rounded-xl hover:bg-gray-200">Cancelar</button>
                        <button type="submit" className="flex-1 py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 shadow-lg">Guardar Cambios</button>
                    </div>
                </form>
            </div>
        </div>
      )}

      {/* --- MODAL BORRAR --- */}
      <ModalSeguridad 
        isOpen={modalOpen} 
        onClose={() => setModalOpen(false)} 
        onConfirm={confirmarBorrado} 
        title={datosEliminar.esZonaEntera ? "Eliminar Zona Completa" : "Eliminar Terminal"} 
        message={datosEliminar.esZonaEntera 
            ? `¿Seguro que quieres borrar la zona "${datosEliminar.zona}" y todas sus terminales?` 
            : `¿Borrar "${datosEliminar.terminal}" de ${datosEliminar.zona}?`} 
        actionType="danger" 
      />
    </div>
  );
};

export default Terminales;